import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Info } from 'lucide-react';
import { formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, Select } from '@/components/ui/field';
import { EmptyState, LoadingState } from '@/components/ui/states';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import { type Device, type TenantStats, type TenantRef, apiError } from './lib';

interface StaffMember {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  isActive: boolean;
}

/**
 * Support tooling. Tenant stats come from the platform module, which takes the
 * subject tenant as a route param. Device and staff endpoints are *tenant*
 * routes, so they are called with an explicit `X-Tenant-Id` header — which
 * `TenantMiddleware` honours only for an operator account that is not itself
 * bound to a tenant (an account's own tenant always wins).
 */
export default function Support() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();

  const [tenants, setTenants] = useState<TenantRef[]>([]);
  const [tenantId, setTenantId] = useState('');
  const [stats, setStats] = useState<TenantStats | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [scopeError, setScopeError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ data: TenantRef[] }>('/tenants', { params: { limit: 200 } })
      .then(({ data }) => setTenants(data.data ?? []))
      .catch(() => setTenants([]));
  }, []);

  const load = useCallback(() => {
    if (!tenantId) {
      setStats(null);
      setDevices([]);
      setStaff([]);
      return;
    }
    setLoading(true);
    setScopeError(null);
    const headers = { 'X-Tenant-Id': tenantId };

    api
      .get<TenantStats>(`/platform/tenants/${tenantId}/stats`)
      .then(({ data }) => setStats(data))
      .catch((e) => {
        setStats(null);
        toast.error(apiError(e, t('platform.support.statsFailed')));
      })
      .finally(() => setLoading(false));

    Promise.all([
      api.get<{ data: Device[] }>('/devices', { params: { limit: 100 }, headers }),
      api.get<{ data: StaffMember[] }>('/users', { params: { limit: 100 }, headers }),
    ])
      .then(([deviceRes, staffRes]) => {
        setDevices(deviceRes.data.data ?? []);
        setStaff(staffRes.data.data ?? []);
      })
      .catch((e) => {
        setDevices([]);
        setStaff([]);
        setScopeError(apiError(e, t('platform.support.scopeFailed')));
      });
  }, [tenantId, t]);

  useEffect(load, [load]);

  const revokeDevice = async (device: Device) => {
    if (!window.confirm(t('platform.support.revokeConfirm', { name: device.name }))) return;
    try {
      await api.post(
        `/devices/${device.id}/revoke`,
        {},
        { headers: { 'X-Tenant-Id': tenantId } },
      );
      toast.success(t('platform.support.deviceRevoked'));
      load();
    } catch (e) {
      toast.error(apiError(e, t('platform.support.revokeFailed')));
    }
  };

  const endSessions = async (member: StaffMember) => {
    const name = `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim() || member.id;
    if (!window.confirm(t('platform.support.endSessionsConfirm', { name }))) return;
    try {
      await api.post(
        `/users/${member.id}/deactivate`,
        {},
        { headers: { 'X-Tenant-Id': tenantId } },
      );
      toast.success(t('platform.support.sessionsEnded'));
      load();
    } catch (e) {
      toast.error(apiError(e, t('platform.support.revokeFailed')));
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-lg font-semibold tracking-tight">{t('platform.support.title')}</h1>
        <p className="text-sm text-[var(--muted-foreground)]">{t('platform.support.subtitle')}</p>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-5">
          <Field label={t('platform.subscriptions.tenant')}>
            <Select
              className="w-full sm:w-72"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
            >
              <option value="">{t('platform.subscriptions.chooseTenant')}</option>
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </option>
              ))}
            </Select>
          </Field>
          <a
            className="pb-2 text-sm text-[var(--accent)] underline"
            href={`${import.meta.env.VITE_API_URL ?? '/api'}/health`}
            target="_blank"
            rel="noreferrer"
          >
            {t('platform.support.healthLink')}
          </a>
        </CardContent>
      </Card>

      {!tenantId ? (
        <EmptyState title={t('platform.support.pickTenant')} hint={t('platform.support.pickTenantHint')} />
      ) : loading ? (
        <LoadingState label={t('common.loading')} />
      ) : (
        <>
          {stats && (
            <Card>
              <CardHeader>
                <CardTitle className="flex flex-wrap items-center gap-2">
                  {stats.tenant.name}
                  <Badge tone={toneForStatus(stats.tenant.status)}>{stats.tenant.status}</Badge>
                </CardTitle>
                <p className="text-xs text-[var(--muted-foreground)]">
                  <code>{stats.tenant.slug}</code> ·{' '}
                  {t('platform.support.createdAt', {
                    date: formatDateTime(stats.tenant.createdAt, i18n.language),
                  })}
                </p>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
                  {(['branches', 'users', 'devices', 'activeSubscriptions'] as const).map((key) => (
                    <div key={key}>
                      <dt className="text-xs text-[var(--muted-foreground)]">
                        {t(`platform.support.count.${key}`)}
                      </dt>
                      <dd className="text-2xl font-semibold">{stats.counts[key]}</dd>
                    </div>
                  ))}
                </dl>
                {stats.subscription && (
                  <p className="mt-4 text-sm">
                    {t('platform.support.runningOn', {
                      plan: stats.subscription.plan?.name ?? stats.subscription.planId,
                      status: t(`platform.subscriptionStatus.${stats.subscription.status}`),
                    })}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {scopeError ? (
            <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p>{scopeError}</p>
                <p className="mt-1 text-xs">{t('platform.support.scopeNote')}</p>
              </div>
            </div>
          ) : (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>{t('platform.support.devices')}</CardTitle>
                  <p className="text-xs text-[var(--muted-foreground)]">
                    {t('platform.support.devicesHint')}
                  </p>
                </CardHeader>
                <CardContent className="p-0">
                  {devices.length === 0 ? (
                    <EmptyState title={t('platform.support.noDevices')} />
                  ) : (
                    <TableWrap className="rounded-none border-x-0 border-t-0">
                      <Table>
                        <THead>
                          <tr>
                            <TH>{t('platform.support.device')}</TH>
                            <TH>{t('platform.support.type')}</TH>
                            <TH>{t('platform.support.branch')}</TH>
                            <TH>{t('platform.support.lastSeen')}</TH>
                            <TH className="text-end">{t('platform.actions')}</TH>
                          </tr>
                        </THead>
                        <tbody>
                          {devices.map((device) => (
                            <TR key={device.id}>
                              <TD>
                                <div className="font-medium">{device.name}</div>
                                {/* An unpaired device has no fingerprint to show,
                                    and `pairing.status` is the field that says
                                    which of the three reasons it is. */}
                                {device.fingerprint ? (
                                  <code className="text-xs text-[var(--muted-foreground)]">
                                    {device.fingerprint.slice(0, 12)}…
                                  </code>
                                ) : (
                                  <span className="text-xs text-[var(--muted-foreground)]">
                                    {device.pairing?.status === 'EXPIRED'
                                      ? t('platform.support.pairingExpired', {
                                          defaultValue: 'Pairing code expired',
                                        })
                                      : t('platform.support.awaitingPairing', {
                                          defaultValue: 'Awaiting pairing',
                                        })}
                                  </span>
                                )}
                              </TD>
                              <TD>{device.type}</TD>
                              <TD>{device.branch?.name ?? '—'}</TD>
                              <TD className="text-[var(--muted-foreground)]">
                                {device.lastSeenAt
                                  ? formatDateTime(device.lastSeenAt, i18n.language)
                                  : '—'}
                              </TD>
                              <TD className="text-end">
                                {device.isActive ? (
                                  can('devices.revoke') && (
                                    <Button
                                      size="sm"
                                      variant="danger"
                                      onClick={() => revokeDevice(device)}
                                    >
                                      {t('platform.support.revoke')}
                                    </Button>
                                  )
                                ) : (
                                  <Badge tone="neutral">{t('platform.support.revoked')}</Badge>
                                )}
                              </TD>
                            </TR>
                          ))}
                        </tbody>
                      </Table>
                    </TableWrap>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{t('platform.support.sessions')}</CardTitle>
                  <p className="text-xs text-[var(--muted-foreground)]">
                    {t('platform.support.sessionsHint')}
                  </p>
                </CardHeader>
                <CardContent className="p-0">
                  {staff.length === 0 ? (
                    <EmptyState title={t('platform.support.noStaff')} />
                  ) : (
                    <TableWrap className="rounded-none border-x-0 border-t-0">
                      <Table>
                        <THead>
                          <tr>
                            <TH>{t('platform.support.staff')}</TH>
                            <TH>{t('platform.support.email')}</TH>
                            <TH>{t('platform.support.state')}</TH>
                            <TH className="text-end">{t('platform.actions')}</TH>
                          </tr>
                        </THead>
                        <tbody>
                          {staff.map((member) => (
                            <TR key={member.id}>
                              <TD>
                                {`${member.firstName ?? ''} ${member.lastName ?? ''}`.trim() ||
                                  member.id}
                              </TD>
                              <TD className="text-[var(--muted-foreground)]">
                                {member.email ?? '—'}
                              </TD>
                              <TD>
                                <Badge tone={member.isActive ? 'success' : 'neutral'}>
                                  {t(
                                    member.isActive
                                      ? 'platform.support.activeStaff'
                                      : 'platform.support.inactiveStaff',
                                  )}
                                </Badge>
                              </TD>
                              <TD className="text-end">
                                {member.isActive && can('users.update') && (
                                  <Button
                                    size="sm"
                                    variant="danger"
                                    onClick={() => endSessions(member)}
                                  >
                                    {t('platform.support.endSessions')}
                                  </Button>
                                )}
                              </TD>
                            </TR>
                          ))}
                        </tbody>
                      </Table>
                    </TableWrap>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}
    </div>
  );
}
